import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchMediaItems,
  uploadMediaItem,
  deleteMediaItem,
  updateMediaItemName,
  setUploadProgress,
  UpdateMediaItemNameResponse,
} from './mediaThunks'
import { MediaItem } from '@/types/media.types'

export interface MediaPaginationState {
  currentPage: number
  pageSize: number
}

export interface MediaState {
  items: MediaItem[]
  isLoading: boolean
  isUploading: boolean
  uploadProgress: number
  uploadError: string | null
  uploadSuccess: boolean
  isDeleting: boolean
  deleteError: string | null
  isUpdating: boolean
  updateError: string | null
  error: string | null
  pagination: MediaPaginationState
}

const initialPagination: MediaPaginationState = {
  currentPage: 1,
  pageSize: 10,
}

const initialState: MediaState = {
  items: [],
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  uploadError: null,
  uploadSuccess: false,
  isDeleting: false,
  deleteError: null,
  isUpdating: false,
  updateError: null,
  error: null,
  pagination: { ...initialPagination },
}

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearMediaError: (state) => {
      state.error = null
      state.uploadError = null
      state.deleteError = null
      state.updateError = null
    },
    clearUploadSuccess: (state) => {
      state.uploadSuccess = false
    },
    setMediaPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload
    },
    setMediaPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload
      state.pagination.currentPage = 1
    },
    resetMediaPagination: (state) => {
      state.pagination = { ...initialPagination }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUploadProgress, (state, action) => {
        state.uploadProgress = action.payload
      })
      .addCase(fetchMediaItems.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.pagination = { ...initialPagination }
      })
      .addCase(fetchMediaItems.fulfilled, (state, action: PayloadAction<MediaItem[]>) => {
        state.isLoading = false
        state.items = action.payload
        state.error = null
        state.pagination = { ...initialPagination }
      })
      .addCase(fetchMediaItems.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || action.error?.message || 'Error al cargar la biblioteca.'
      })
      .addCase(uploadMediaItem.pending, (state) => {
        state.isUploading = true
        state.uploadError = null
        state.uploadSuccess = false
        state.uploadProgress = 0
      })
      .addCase(uploadMediaItem.fulfilled, (state, action: PayloadAction<MediaItem>) => {
        state.isUploading = false
        state.items.unshift(action.payload)
        state.uploadSuccess = true
        state.uploadError = null
        state.uploadProgress = 100
        // El archivo recién subido aparece al tope (orden por createdAt DESC).
        // Saltar a página 1 para que sea visible sin que el usuario tenga que navegar.
        state.pagination.currentPage = 1
      })
      .addCase(uploadMediaItem.rejected, (state, action) => {
        state.isUploading = false
        state.uploadError = action.payload || action.error?.message || 'Error al subir el archivo.'
        state.uploadSuccess = false
        state.uploadProgress = 0
      })
      .addCase(deleteMediaItem.pending, (state) => {
        state.isDeleting = true
        state.deleteError = null
      })
      .addCase(deleteMediaItem.fulfilled, (state, action: PayloadAction<string>) => {
        state.isDeleting = false
        state.items = state.items.filter((i) => i.id !== action.payload)
        state.deleteError = null
        // Si tras eliminar la página actual queda fuera de rango, ajustar al último valor válido
        // para evitar mostrar una página vacía con el usuario "atrapado" navegando hacia atrás.
        const maxPage = Math.max(1, Math.ceil(state.items.length / state.pagination.pageSize))
        if (state.pagination.currentPage > maxPage) {
          state.pagination.currentPage = maxPage
        }
      })
      .addCase(deleteMediaItem.rejected, (state, action) => {
        state.isDeleting = false
        state.deleteError = action.payload || action.error?.message || 'Error al eliminar el archivo.'
      })
      .addCase(updateMediaItemName.pending, (state) => {
        state.isUpdating = true
        state.updateError = null
      })
      .addCase(updateMediaItemName.fulfilled, (state, action: PayloadAction<UpdateMediaItemNameResponse>) => {
        state.isUpdating = false
        const idx = state.items.findIndex((i) => i.id === action.payload.id)
        if (idx >= 0) {
          state.items[idx] = {
            ...state.items[idx],
            name: action.payload.name,
            updatedAt: action.payload.updatedAt,
          }
        }
        state.updateError = null
      })
      .addCase(updateMediaItemName.rejected, (state, action) => {
        state.isUpdating = false
        state.updateError = action.payload || action.error?.message || 'Error al actualizar el archivo.'
      })
  },
})

export const {
  clearMediaError,
  clearUploadSuccess,
  setMediaPage,
  setMediaPageSize,
  resetMediaPagination,
} = mediaSlice.actions
export default mediaSlice.reducer
